import { action, observable, store } from '~/common/mobx.decorator'
import { apolloClient, CONTEXT } from '~/common/apollo'
import { CREATE_MARKETING_URL_TRACKING } from './utm-tracking.mutation'
import { authStore } from '~/stores'

const UTM_STORAGE_KEY = 'utm_tracking_data'
const EXPIRED_TIME = 30 * 60 * 1000 // 30 minutes in milliseconds

@store()
class UtmTrackingStore {
	@observable utmParams = null
	@observable loading = false
	@observable isTracking = false // Add flag to prevent multiple tracking

	@action
	setUtmParams(params) {
		this.utmParams = params
		// Save to localStorage with timestamp
		localStorage.setItem(
			UTM_STORAGE_KEY,
			JSON.stringify({
				url: params,
				timestamp: Date.now(),
			})
		)
	}

	@action
	clearUtmParams() {
		this.utmParams = null
		this.isTracking = false
		localStorage.removeItem(UTM_STORAGE_KEY)
	}

	getStoredUtmData() {
		const storedData = localStorage.getItem(UTM_STORAGE_KEY)
		if (!storedData) return null

		const { url, timestamp } = JSON.parse(storedData)

		// Return null if expired
		if (Date.now() - timestamp > EXPIRED_TIME) {
			this.clearUtmParams()
			return null
		}

		return url
	}

	@action
	setLoading(status) {
		this.loading = status
	}

	@action
	async trackUtm(url) {
		if (this.isTracking) return // Prevent multiple tracking
		if (authStore.isAnonymous) return // Don't track if not authenticated

		try {
			this.isTracking = true
			this.setLoading(true)
			await apolloClient.mutate({
				mutation: CREATE_MARKETING_URL_TRACKING,
				variables: {
					url,
				},
			})
			this.clearUtmParams() // Clear after successful API call
		} catch (error) {
			console.error('Failed to track marketing URL:', error)
		} finally {
			this.setLoading(false)
			this.isTracking = false
		}
	}

	@action
	async trackUtmWithToken(url, token) {
		if (this.isTracking) return

		try {
			this.isTracking = true
			this.setLoading(true)
			await apolloClient.mutate({
				mutation: CREATE_MARKETING_URL_TRACKING,
				variables: {
					url,
				},
				context: {
					headers: {
						Authorization: `${token}`.trim(),
						'tenant-id': process.env.REACT_APP_COMPANY_ID,
					},
				},
			})
			this.clearUtmParams()
		} catch (error) {
			console.error('Failed to track marketing URL:', error?.message, error?.networkError?.result)
		} finally {
			this.setLoading(false)
			this.isTracking = false
		}
	}
}

export const utmTrackingStore = new UtmTrackingStore()
